import { useEffect } from "react";

import { IgButton, IgTypography } from "@intelligems/ig-theme";
import Dialog from "@mui/material/Dialog";

import {
  AI_USER_INPUT_PRODUCT_PICKER_TYPE,
  ShopifyEntityPick,
} from "@src/features/ai/components/WorkflowEditor/execution/panel/shopifyEntityPickerCopy";

import { ProductPicker } from "@src/components/MainApp/SharedProductPicker/ProductPicker";
import { useDispatch, useSelector } from "@src/store";
import {
  closeProductPicker,
  getProductPickerSelectedProducts,
  openProductPicker,
} from "@src/store/slices/productPicker";
import { isProductPickerOpen } from "@src/store/slices/productPicker/selectors/isProductPickerOpen";

interface ShopifyProductPickerDialogProps {
  multiSelect: boolean;
  open: boolean;
  /** Blocks Done — e.g. while the chat is streaming or an upload is settling. */
  saveDisabled?: boolean;
  onClose: () => void;
  onSave: (picked: ShopifyEntityPick[]) => void;
}

/**
 * The picker a `product` shopify-pick question opens. Every flat kind is
 * picked inline from the card (see `ShopifyEntityCombobox`); products keep the
 * shared `ProductPicker`, whose images, variants and filters have no inline
 * equivalent.
 *
 * The product picker's open state lives in the store, keyed by type, so this
 * mirrors `open` into it: opening dispatches `openProductPicker`, and any way
 * out (Cancel, Done, backdrop) dispatches `closeProductPicker`, which also
 * clears the running selection. Products are always multi-select in the
 * shared picker, so a single-answer question is enforced at Done, the way the
 * cart-test picker caps its count.
 */
export const ShopifyProductPickerDialog = ({
  multiSelect,
  open,
  saveDisabled = false,
  onClose,
  onSave,
}: ShopifyProductPickerDialogProps) => {
  const dispatch = useDispatch();
  const pickerOpen = useSelector(
    isProductPickerOpen(AI_USER_INPUT_PRODUCT_PICKER_TYPE)
  );
  const selectedProducts = useSelector(getProductPickerSelectedProducts);

  useEffect(() => {
    if (open && !pickerOpen) {
      dispatch(openProductPicker({ type: AI_USER_INPUT_PRODUCT_PICKER_TYPE }));
    }
  }, [open, pickerOpen, dispatch]);

  const close = () => {
    dispatch(closeProductPicker());
    onClose();
  };
  const tooMany = !multiSelect && selectedProducts.length > 1;

  return (
    <Dialog
      disableEscapeKeyDown
      fullWidth
      maxWidth="lg"
      open={open && pickerOpen}
      sx={{ zIndex: 1401 }}
      onClose={close}
      slotProps={{ paper: { style: { maxHeight: "700px" } } }}
    >
      <ProductPicker
        loadVariants={false}
        dialogActions={[
          ...(tooMany
            ? [
                <IgTypography key="cap" variant="caption" color="error">
                  Pick one product
                </IgTypography>,
              ]
            : []),
          <IgButton key="cancel" variant="text" onClick={close}>
            Cancel
          </IgButton>,
          <IgButton
            key="done"
            variant="contained"
            disabled={selectedProducts.length === 0 || tooMany || saveDisabled}
            onClick={() => {
              onSave(
                selectedProducts.map((product) => ({
                  id: product.id,
                  label: product.title,
                }))
              );
              dispatch(closeProductPicker());
            }}
          >
            Done
          </IgButton>,
        ]}
      />
    </Dialog>
  );
};
